import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Construction,
  Analytics,
  TrendingUp,
  CheckCircle,
  Schedule,
  HourglassEmpty,
  Category,
  CalendarToday,
  Message
} from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import { getDashboardStats } from '../../utils/api';

const StatCard = styled(Card)(({ theme }) => ({
  background: '#2C3E50', // Professional dark background
  color: 'white',
  height: '100%',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const AnalyticsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProjects: 0,
    completedProjects: 0,
    ongoingProjects: 0,
    upcomingProjects: 0,
    totalMessages: 0,
    unreadMessages: 0,
    readMessages: 0,
    totalServices: 0,
    recentActivity: 0,
    projectsByStatus: [],
    projectsByCategory: [],
    messagesTrends: []
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await getDashboardStats();

      if (response.data && response.data.success) {
        setStats(response.data.stats);
        setRecentMessages(response.data.stats.recentMessages || []);
      } else {
        // Fall back to default data if API fails
        setStats({
          totalProjects: 0,
          completedProjects: 0,
          ongoingProjects: 0,
          upcomingProjects: 0,
          totalMessages: 0,
          unreadMessages: 0,
          readMessages: 0,
          totalServices: 0,
          recentActivity: 0,
          projectsByStatus: [],
          projectsByCategory: [],
          messagesTrends: []
        });
      }
    } catch (error) {
      // Fall back to default data if API fails
      setStats({
        totalProjects: 0,
        completedProjects: 0,
        ongoingProjects: 0,
        upcomingProjects: 0,
        totalMessages: 0,
        unreadMessages: 0,
        readMessages: 0,
        totalServices: 0,
        recentActivity: 0,
        projectsByStatus: [],
        projectsByCategory: [],
        messagesTrends: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getProjectStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle color="success" />;
      case 'ongoing': return <Schedule color="warning" />;
      case 'upcoming': return <HourglassEmpty color="info" />;
      default: return <HourglassEmpty />;
    }
  };

  const getProjectCompletion = () => {
    const total = stats.totalProjects;
    if (total === 0) return 0;
    return Math.round((stats.completedProjects / total) * 100);
  };

  return (
    <AdminLayout title="Dashboard">
      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" gutterBottom sx={{
            fontWeight: 600,
            fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' }
          }}>
            Analytics Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Overview of your construction company's performance and data
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Main Stats Overview */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Construction sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalProjects}
                    </Typography>
                    <Typography variant="body2">
                      Total Projects
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard sx={{ background: '#3498DB' }}> {/* Modern blue */}
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Message sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalMessages}
                    </Typography>
                    <Typography variant="body2">
                      Total Messages
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard sx={{ background: '#5D6D7E' }}> {/* Professional gray */}
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Category sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalServices}
                    </Typography>
                    <Typography variant="body2">
                      Total Services
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
            </Grid>

            {/* Project Analytics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <AnalyticsCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Analytics color="primary" />
                      Projects by Status
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle color="success" fontSize="small" />
                          <Typography variant="body2">Completed</Typography>
                        </Box>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                          {stats.completedProjects}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stats.totalProjects > 0 ? (stats.completedProjects / stats.totalProjects) * 100 : 0}
                        sx={{ height: 8, borderRadius: 1 }}
                        color="success"
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Schedule color="warning" fontSize="small" />
                          <Typography variant="body2">Ongoing</Typography>
                        </Box>
                        <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                          {stats.ongoingProjects}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stats.totalProjects > 0 ? (stats.ongoingProjects / stats.totalProjects) * 100 : 0}
                        sx={{ height: 8, borderRadius: 1 }}
                        color="warning"
                      />
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HourglassEmpty color="info" fontSize="small" />
                          <Typography variant="body2">Upcoming</Typography>
                        </Box>
                        <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                          {stats.upcomingProjects}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stats.totalProjects > 0 ? (stats.upcomingProjects / stats.totalProjects) * 100 : 0}
                        sx={{ height: 8, borderRadius: 1 }}
                        color="info"
                      />
                    </Box>
                  </CardContent>
                </AnalyticsCard>
              </Grid>
            </Grid>

            {/* Project Categories and Recent Activity */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {stats.projectsByCategory && stats.projectsByCategory.length > 0 && (
                <Grid item xs={12} md={6}>
                  <AnalyticsCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Category color="primary" />
                        Projects by Category
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <List dense>
                        {stats.projectsByCategory.map((category, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2">{category.category}</Typography>
                                  <Chip
                                    label={category.count}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </AnalyticsCard>
                </Grid>
              )}

              <Grid item xs={12} md={stats.projectsByCategory && stats.projectsByCategory.length > 0 ? 6 : 12}>
                <AnalyticsCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday color="primary" />
                      Recent Activity
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {recentMessages.length > 0 ? (
                      <List dense>
                        {recentMessages.slice(0, 5).map((message, index) => (
                          <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  Message from {message.name}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {message.email} • {formatDate(message.created_at)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    "{message.message.substring(0, 60)}..."
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Alert severity="info">
                        No recent messages. Your business is all caught up!
                      </Alert>
                    )}
                  </CardContent>
                </AnalyticsCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </AdminLayout>
  );
};

export default Dashboard;